import React, { useState, useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';
import { restanteMs, formataRestante } from '../utils/countdown';

/**
 * Contador regressivo até um instante absoluto.
 *
 * Regras que valem a pena registrar, porque errar qualquer uma delas produz um
 * contador que mente:
 *
 *  - a conta é sempre `endsAt - agora`, calculada a cada tique a partir do
 *    relógio. Nada de decrementar um número guardado: um refresh, uma aba em
 *    segundo plano ou o navegador suspendendo o timer fariam o contador
 *    divergir do horário real de término;
 *  - nada é persistido. O instante de fim vem do servidor, e o localStorage só
 *    conseguiria guardar uma cópia desatualizada dele;
 *  - `endsAt` ausente ou inválido devolve `null`, e o componente não renderiza.
 *    Um "23:59" inventado seria pior que contador nenhum;
 *  - o intervalo é limpo no unmount e quando o prazo acaba.
 *
 * O `Date` do JavaScript entende o offset da string ISO, então um `endsAt` em
 * UTC vindo do backend é comparado corretamente com o relógio local do visitante.
 */

export default function CountdownTimer({
    endsAt,
    prefixo = 'TERMINA EM',
    onExpirar,
    compacto = false,
    className = '',
    // 'urgente' usa o coral de urgência da vitrine. O padrão mantém a aparência
    // de antes, que é a usada no admin.
    tom = 'padrao',
}) {
    const [ms, setMs] = useState(() => restanteMs(endsAt));
    const jaAvisou = useRef(false);

    useEffect(() => {
        jaAvisou.current = false;
        setMs(restanteMs(endsAt));
        if (restanteMs(endsAt) === null) return undefined;

        const id = setInterval(() => {
            const falta = restanteMs(endsAt);
            setMs(falta);
            if (falta === 0 && !jaAvisou.current) {
                jaAvisou.current = true;
                // Quem chamou decide o que fazer: normalmente refetch, para que o
                // card saia da vitrine pelo estado, e não por manipulação do DOM.
                if (typeof onExpirar === 'function') onExpirar();
            }
        }, 1000);

        return () => clearInterval(id);
    }, [endsAt, onExpirar]);

    // Sem prazo real não há o que contar.
    if (ms === null) return null;

    const expirado = ms === 0;
    const base = compacto
        ? 'inline-flex items-center gap-1 text-[10px] font-bold tabular-nums'
        : 'inline-flex items-center gap-1.5 text-xs font-bold tabular-nums px-2.5 py-1 rounded-full';

    return (
        <span
            className={`${base} ${className}`}
            data-testid="countdown-timer"
            data-expirado={expirado ? '1' : '0'}
            style={tom === 'urgente'
                ? (compacto
                    ? { color: expirado ? 'var(--muted-2)' : 'var(--sv-discount)' }
                    : {
                        background: expirado ? 'var(--surface-2)' : 'rgba(224,45,75,0.16)',
                        border: `1px solid ${expirado ? 'var(--border)' : 'rgba(255,90,112,0.45)'}`,
                        color: expirado ? 'var(--muted-2)' : '#FFD2D9',
                    })
                : (compacto ? { color: expirado ? 'var(--muted-2)' : 'var(--gold-2)' } : {
                    background: expirado ? 'var(--surface-2)' : 'rgba(var(--sv-accent-rgb), 0.12)',
                    border: `1px solid ${expirado ? 'var(--border)' : 'rgba(var(--sv-accent-rgb), 0.35)'}`,
                    color: expirado ? 'var(--muted-2)' : 'var(--gold-2)',
                })}
        >
            <Clock size={compacto ? 10 : 12} />
            {expirado ? 'ENCERRADA' : `${prefixo} ${formataRestante(ms)}`}
        </span>
    );
}
